import React, { useEffect, useState, useRef } from 'react';
import * as Inovx from '@shared';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import {
    BackgroundColorModel,
    ChangeEventArgs,
    FontColorModel,
    HtmlEditor,
    Image,
    ImageSettingsModel,
    Inject,
    QuickToolbar,
    QuickToolbarSettingsModel,
    RichTextEditorComponent,
    Toolbar
} from '@syncfusion/ej2-react-richtexteditor';
import KnowledgeBookStore from '../../stores/KnowledgeBookStore';
import KnowledgeBookActions from '../../actions/KnowledgeBookActions';
import KnowledgeBookConstants from '@facility/constants/KnowledgeBookConstants';
import colorPickerPalette from './colorPickerPalette';
import useAsyncActionLock from '@shared/hooks/useAsyncActionLock';
var InovxControls = Inovx.Shared.Controls;
var SystemStore = Inovx.Shared.System.Store;

const MAX_KNOWLEDGE_VIEW_IMAGE_SIZE = 5 * 1024 * 1024;

/* ------------------ COMPONENT ------------------ */
interface KnowledgeViewProps {

}
const KnowledgeViewForm: React.FC<KnowledgeViewProps> = ({ }) => {
    const rteRef = useRef<RichTextEditorComponent>(null);
    const [kVForm, setKVForm] = useState<IKnowledgeView | null>(KnowledgeBookStore.getKnowledgeViewDetail());
    const [templateFormErrors, setTemplateFormErrors] = useState<IKnowledgeViewFormErrorProps>(KnowledgeBookStore.getValidation());
    const [isActionInProgress, setActionInProgress] = useState<boolean>(false);

    const getIsActionInProgress = ()=> {
        return SystemStore.isActionInProgress(KnowledgeBookConstants.KNOWLEDGE_VIEW_EDIT);
    }

    const systemChange = () => {
        setActionInProgress(getIsActionInProgress())
    }
    const loadState = () => {
        setTemplateFormErrors(KnowledgeBookStore.getValidation());
        setKVForm(KnowledgeBookStore.getKnowledgeViewDetail())
    };

    useEffect(() => {
        SystemStore.addChangeListener(systemChange);
        KnowledgeBookStore.addChangeListener(loadState);
        return () => {
            KnowledgeBookStore.removeChangeListener(loadState);
            SystemStore.removeChangeListener(systemChange);
        };
    }, []);

    const saveKnowledgeView = async () => {
        await KnowledgeBookActions.setKnowledgeViewProperties();
    }
    const saveKnowledgeViewAction = useAsyncActionLock(saveKnowledgeView);

    const handleBeforeClose = (args: any) => {
        if (KnowledgeBookStore.getHasChanges()) {
            closeAddKV();
            args.cancel = true;
            return;
        } else {
            closeAddKV();
        }
    };

    const closeAddKV = () => {
        KnowledgeBookActions.finishKnowledgeViewEdit();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const currentForm = KnowledgeBookStore.getKnowledgeViewDetail();
        if (currentForm) {
            const updatedForm = { ...currentForm, [name]: value };
            KnowledgeBookActions.updateKnowledgeViewInStore(updatedForm);
        }
    };

    const editorChange = (e: ChangeEventArgs) => {
        if (e.name === 'change') {
            const currentForm = KnowledgeBookStore.getKnowledgeViewDetail();
            if (currentForm) {
                const updatedForm = { ...currentForm, Description: e.value };
                KnowledgeBookActions.updateKnowledgeViewInStore(updatedForm);
            }
        }
    }

    const toolbarSettings: object = {
        enable: true,
        items: ['Bold', 'Italic', 'Underline', 'StrikeThrough',
            'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
            'Alignments', 'Outdent', 'Indent', '|',
            'Image', '|', 'Undo', 'Redo'],
        type: 'Scrollable'
    };
    const insertImageSettings: ImageSettingsModel = {
        allowedTypes: ['.jpeg', '.jpg', '.png'],
        maxFileSize: MAX_KNOWLEDGE_VIEW_IMAGE_SIZE,
        resize: true,
        saveFormat: 'Base64'
    };
    const quickToolbarSettings: QuickToolbarSettingsModel = {
        image: ['AltText', 'Align', 'Display', '|', 'Dimension', 'Replace', 'Remove']
    };
    const fontColor: FontColorModel = {
        mode: 'Picker',
        modeSwitcher: true,
        columns: 10,
        colorCode: colorPickerPalette,
        showRecentColors: false
    };

    const backgroundColor: BackgroundColorModel = {
        mode: 'Picker',
        modeSwitcher: true,
        columns: 10,
        colorCode: colorPickerPalette,
        showRecentColors: false
    };
    const renderHeader = () => (
        <div>
            <div className="e-icon-settings dlg-template">
                {InovxLoader.t("facility:knowledgeBook.knowledgeView")}
            </div>
        </div>
    );
    const renderFooter = () => (
        <div>
            <div className="d-flex j-cc">
                <div>
                    <button
                        id="facilityApp-save-btn"
                        onClick={() => void saveKnowledgeViewAction.run()}
                        className="btn btn-primary"
                        disabled={isActionInProgress || saveKnowledgeViewAction.isInFlight}
                    >
                        {InovxLoader.t("facility:preferences.saveButton")}
                    </button>
                </div>
                <div className="ml-1">
                    <button
                        id="facilityApp-cancel-btn"
                        onClick={closeAddKV}
                        className="btn btn-default"
                        disabled={saveKnowledgeViewAction.isInFlight}
                    >
                        {InovxLoader.t("facility:preferences.cancelButton")}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <DialogComponent
            target={'#app'}
            width='550px'
            header={renderHeader}
            footerTemplate={renderFooter}
            showCloseIcon={true}
            visible={true}
            beforeClose={handleBeforeClose}
            isModal={true}
            zIndex={110000}
        >
            <div className="dialogContent">

                <InovxControls.FormField
                    title={InovxLoader.t("facility:knowledgeBook.name")}
                    inputName='Name'
                    className={`${templateFormErrors.Name ? 'has-error' : ''}`}
                >
                    <input
                        type="text"
                        onChange={handleInputChange}
                        value={kVForm.Name || ''}
                        className="form-control"
                        name="Name"
                        placeholder={InovxLoader.t("facility:form.knowledgeViewNamePlaceholder")}
                        autoComplete="off"
                    />
                </InovxControls.FormField>

                <InovxControls.FormField
                    title={InovxLoader.t("facility:knowledgeBook.knowledgeBookDescription")}
                    inputName='Description'
                    className={`mt-1 ${templateFormErrors.Description ? 'has-error' : ''}`}
                >
                    <div className='w-100' >
                        <RichTextEditorComponent
                            ref={rteRef}
                            autoSaveOnIdle={true}
                            saveInterval={100}
                            change={editorChange}
                            value={kVForm?.Description}
                            height={250}
                            cssClass="kv-description-editor"
                            toolbarSettings={toolbarSettings}
                            quickToolbarSettings={quickToolbarSettings}
                            insertImageSettings={insertImageSettings}
                            fontColor={fontColor}
                            backgroundColor={backgroundColor}
                        >
                            <Inject services={[Toolbar, HtmlEditor, Image, QuickToolbar]} />
                        </RichTextEditorComponent>
                    </div>
                </InovxControls.FormField>
            </div>
        </DialogComponent>
    );
};

export default KnowledgeViewForm;
